import React from 'react'
import PropTypes from 'prop-types'
import { FILE_KIND_LABEL } from '@src/services/consentSettings'
import { fileStatusChip } from '../constants'
import { cellState } from '../fileMatcher'
import { isActive } from '../uploadQueue'
import StatusChip from './StatusChip'

const actionButton =
  'text-xs font-semibold text-brand/700 hover:underline ' +
  'disabled:cursor-not-allowed disabled:text-gray-400 disabled:no-underline'

// The upload state of a file being saved, over its "Not saved yet" chip.
function queueChip(item) {
  switch (item?.state) {
    case 'queued':
      return { label: 'Waiting to upload', color: 'gray' }
    case 'uploading':
      return { label: `Uploading ${Math.round((item.progress || 0) * 100)}%`, color: 'primary' }
    case 'processing':
      return { label: 'Processing…', color: 'primary' }
    case 'failed':
      return { label: 'Not saved', color: 'error' }
    case 'not_sent':
      return { label: 'Not sent', color: 'gray' }
    default:
      return null
  }
}

const rowsText = (rowNos) => `row${rowNos.length > 1 ? 's' : ''} ${rowNos.join(', ')}`

/**
 * One file of one row in the file grid: what the API says about it
 * (files[kind]), or the file added in this browser for it (`entry`) and its
 * upload (`item`), with what HR can do there.
 */
export default function FileCell({
  row,
  kind,
  entry,
  item,
  duplicate,
  locked,
  onPreview,
  onChoose,
  onRemoveSaved,
  onReassign,
  onDiscard,
  onRetry,
}) {
  const state = cellState(row, kind, entry)
  if (state === 'none') return <span className="text-sm text-gray-400">—</span>

  const label = FILE_KIND_LABEL[kind] || kind
  const saved = row.files?.[kind]
  const where = `${label} of row ${row.row_no}`
  const active = isActive(item)
  const chip = (state === 'pending' && queueChip(item)) || fileStatusChip(state)
  const fileName = state === 'pending' ? entry.file.name : saved?.file_name

  const actions = []
  const action = (key, text, onClick, disabled = false, aria = `${text} ${where}`) =>
    actions.push(
      <button key={key} type="button" className={actionButton} onClick={onClick} disabled={disabled} aria-label={aria}>
        {text}
      </button>
    )

  if (state === 'pending') {
    action('preview', 'Preview', () => onPreview({ kind, row, file: entry.file }))
    if (item?.state === 'failed' && item.error?.retryable) action('retry', 'Retry', () => onRetry(entry))
    action('reassign', 'Reassign', () => onReassign(entry), locked || active)
    action('discard', 'Discard', () => onDiscard(entry), locked || active)
  } else if (state === 'missing') {
    action('choose', 'Choose file', () => onChoose(row, kind), locked)
  } else {
    if (saved?.url) action('preview', 'Preview', () => onPreview({ kind, row, saved }))
    if (state === 'staged') action('replace', 'Replace', () => onChoose(row, kind), locked)
    if (state === 'staged' || state === 'leftover') action('remove', 'Remove', () => onRemoveSaved(row, kind), locked)
  }

  const notes = []
  if (state === 'pending') {
    if (item?.state === 'uploading') {
      const percent = Math.round((item.progress || 0) * 100)
      notes.push(
        <div
          key="progress"
          role="progressbar"
          aria-label={`Upload of the ${where}`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
          className="h-1 w-full overflow-hidden rounded bg-gray-100"
        >
          <div className="h-1 rounded bg-brand/600 transition-all" style={{ width: `${percent}%` }} />
        </div>
      )
    }
    if (item?.state === 'failed' && item.error?.message) {
      notes.push(
        <span key="error" className="text-xs text-error/700">
          {item.error.message}
        </span>
      )
    }
    if (item?.state === 'not_sent') {
      notes.push(
        <span key="not-sent" className="text-xs text-gray-500">
          Saving stopped before this file was sent.
        </span>
      )
    }
    if (duplicate?.blocking) {
      notes.push(
        <span key="dup" className="text-xs text-error/700">
          The same photo is on {rowsText(duplicate.rowNos)}: a KTP photo belongs to one person. Discard or
          reassign one of them.
        </span>
      )
    } else if (duplicate) {
      const said = [
        duplicate.rowNos.length ? `Same file as ${rowsText(duplicate.rowNos)}.` : null,
        duplicate.sameRow ? "Same file as this row's other document." : null,
      ]
      notes.push(
        <span key="dup" className="text-xs text-warning/700">
          {said.filter(Boolean).join(' ')}
        </span>
      )
    }
    if (entry.replaces && !active) {
      notes.push(
        <span key="replaces" className="text-xs text-gray-500">
          Replaces the saved file.
        </span>
      )
    }
  } else if (saved?.duplicate_of_rows?.length) {
    notes.push(
      <span key="saved-dup" className="text-xs text-warning/700">
        Same file as {rowsText(saved.duplicate_of_rows)}.
      </span>
    )
  }
  if (state === 'reused') {
    notes.push(
      <span key="reused" className="text-xs text-gray-500">
        The employee&apos;s e-KTP on file.
      </span>
    )
  }

  return (
    <div className="flex min-w-[170px] max-w-[240px] flex-col items-start gap-1 py-1">
      <StatusChip {...chip} />
      {fileName && (
        <span className="max-w-full truncate text-xs text-gray-600" title={fileName}>
          {fileName}
        </span>
      )}
      {notes}
      {actions.length > 0 && <div className="flex flex-wrap gap-x-3 gap-y-1">{actions}</div>}
    </div>
  )
}

FileCell.propTypes = {
  row: PropTypes.object.isRequired,
  kind: PropTypes.string.isRequired,
  entry: PropTypes.object,
  item: PropTypes.object,
  duplicate: PropTypes.shape({
    blocking: PropTypes.bool,
    rowNos: PropTypes.arrayOf(PropTypes.number),
    sameRow: PropTypes.bool,
  }),
  locked: PropTypes.bool,
  onPreview: PropTypes.func.isRequired,
  onChoose: PropTypes.func.isRequired,
  onRemoveSaved: PropTypes.func.isRequired,
  onReassign: PropTypes.func.isRequired,
  onDiscard: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
}
